import type { TrainingTimeOfDay } from '@cadencia/contracts';
import { requireExercise, MUSCLE_LABELS, resolveTrainingTimeOfDay } from '@cadencia/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Boton, BotonEnlace, Cabecera, Chip, Estado, Tarjeta } from '../../components/ui.js';
import { IconoSustituir } from '../../components/iconos.js';
import { useAppData } from '../../data/DataProvider.js';
import { getCheckinForDate } from '../../data/repositories/readinessRepo.js';
import { getSessionDetail, setSessionTrainingTime } from '../../data/repositories/trainingRepo.js';
import { usePreferencias } from '../../data/preferenciaIA.js';
import { formatLongDate, todayLocal } from '../../lib/dates.js';
import { mensajeDeError } from '../../lib/errores.js';
import { SustituirEjercicio } from './SustituirEjercicio.js';
import './entrenar.css';

/**
 * Previsualización antes de empezar (requisito §45): nombre, objetivo,
 * ejercicios, series, repeticiones, RIR, descansos y duración aproximada.
 */
export function SesionPreviewPage(): ReactNode {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { ctx, ready } = useAppData();
  const navigate = useNavigate();
  const [sustituyendo, setSustituyendo] = useState<string | null>(null);
  const [cambiandoTurno, setCambiandoTurno] = useState(false);
  const [errorTurno, setErrorTurno] = useState<string | null>(null);
  const preferencias = usePreferencias(ctx?.userId ?? null);

  const detalle = useLiveQuery(
    async () => (sessionId ? getSessionDetail(sessionId) : null),
    [sessionId],
  );

  const checkin = useLiveQuery(
    async () => (ctx ? getCheckinForDate(ctx.userId, todayLocal()) : undefined),
    [ctx?.userId],
  );

  if (!ready || detalle === undefined) return <Estado tipo="cargando" titulo="Cargando sesión" />;
  if (detalle === null) {
    return (
      <Estado
        tipo="error"
        titulo="No se encuentra la sesión"
        accion={
          <BotonEnlace a="/entrenar" variante="secundario">Volver</BotonEnlace>
        }
      />
    );
  }

  const { session, exercises } = detalle;
  const seriesTotales = exercises.reduce((total, item) => total + item.prescriptions.length, 0);
  const yaCompletada = session.status === 'completed';
  const esFuerza = session.sessionKind === 'strength' || session.sessionKind === 'strength_plus_cardio';

  /**
   * El turno de ESTA sesion, resuelto.
   *
   * `session.trainingTimeOfDay` manda cuando esta puesto; si no, el habitual de
   * las preferencias. La resolucion la hace el dominio y no esta pantalla:
   * `resolveTrainingTimeOfDay` es la unica funcion que mira los dos campos.
   */
  const turno: TrainingTimeOfDay = resolveTrainingTimeOfDay(
    session.trainingTimeOfDay,
    preferencias?.fila?.defaultTrainingTimeOfDay ?? 'afternoon',
  );

  const cambiarTurno = (nuevo: TrainingTimeOfDay): void => {
    if (ctx === null) return;
    setCambiandoTurno(true);
    setErrorTurno(null);
    void (async () => {
      try {
        // Cambia la sesion y rehace el menu que dependia de ella. Lo ya
        // registrado no se toca: el corte esta en hoy (§33).
        await setSessionTrainingTime(ctx, session, nuevo);
      } catch (caught) {
        setErrorTurno(mensajeDeError(caught));
      } finally {
        setCambiandoTurno(false);
      }
    })();
  };

  const empezar = (): void => {
    // El check-in de readiness va antes de la sesión: modula la carga de hoy.
    navigate(
      checkin ? `/entrenar/sesion/${session.id}/activa` : `/entrenar/sesion/${session.id}/readiness`,
    );
  };

  return (
    <>
      <header className="sesion-cabecera">
        {yaCompletada && <Chip>Completada</Chip>}
      </header>

      <Cabecera
        titulo={session.name}
        encima={formatLongDate(session.scheduledDate)}
        descripcion={session.objective}
        volver={{ a: '/entrenar', etiqueta: 'Entrenar' }}
      />

      <div className="resumen-sesion">
        <Chip tono="neutro">{exercises.length} ejercicios</Chip>
        <Chip tono="neutro">{seriesTotales} series</Chip>
        <Chip tono="neutro">~{session.estimatedMinutes} min</Chip>
      </div>

      <div className="lista-ejercicios">
        {exercises.map((item) => {
          const ejercicio = requireExercise(item.sessionExercise.exerciseSlug);
          const primera = item.prescriptions[0];
          return (
            <div key={item.sessionExercise.id}>
              {/*
                Dos lineas en movil y una en escritorio, y no por gusto: con el
                nombre y la prescripcion compitiendo por el ancho de 393 px,
                «Press de hombro con mancuernas» se partia en TRES lineas
                mientras «2×10-12 RIR 4 · 120s» se quedaba entero al lado. Una
                rejilla con areas mueve la prescripcion debajo del nombre en
                movil sin tocar el orden de lectura.
              */}
              <div className="ejercicio-fila ejercicio-fila--plan">
                <span className="ejercicio-fila__numero">{item.sessionExercise.orderIndex}</span>
                <span className="ejercicio-fila__nombre">
                  {ejercicio.nameEs}
                  <span className="ejercicio-fila__musculo">
                    {' '}
                    · {ejercicio.primaryMuscles.map((m) => MUSCLE_LABELS[m]).join(', ')}
                  </span>
                  {item.sessionExercise.substitutedFromSlug !== null && (
                    <span className="ejercicio-fila__musculo"> · sustituido</span>
                  )}
                </span>
                <span className="ejercicio-fila__series numeros">
                  {item.prescriptions.length}×{primera?.targetRepsMin}-{primera?.targetRepsMax}
                  <span className="ejercicio-fila__descanso">
                    {' '}
                    RIR {primera?.targetRir} · {item.sessionExercise.targetRestSeconds}s
                  </span>
                </span>
                {!yaCompletada && (
                  <Boton
                    variante="texto"
                    className="ejercicio-fila__sustituir"
                    aria-label={`Sustituir ${ejercicio.nameEs}`}
                    aria-expanded={sustituyendo === item.sessionExercise.id}
                    onClick={() =>
                      setSustituyendo((actual) =>
                        actual === item.sessionExercise.id ? null : item.sessionExercise.id,
                      )
                    }
                  >
                    <IconoSustituir />
                  </Boton>
                )}
              </div>

              {sustituyendo === item.sessionExercise.id && (
                <SustituirEjercicio
                  sessionExercise={item.sessionExercise}
                  onHecho={() => setSustituyendo(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* El turno solo se pregunta donde significa algo: una sesion de fuerza
          que todavia no se ha hecho: en una completada el turno ya lo decidio
          el reloj, y ofrecer cambiarlo seria invitar a falsear el historial. En
          una de cardio no aporta nada. */}
      {esFuerza && !yaCompletada && (
        <section className="turno-sesion">
          <h2 className="turno-sesion__titulo">¿Cuándo entrenas este día?</h2>
          <p className="turno-sesion__pista">
            Queda anotado con la sesión. Entrenar a primera hora o al final del día no
            es el mismo escenario para el mismo peso.
          </p>
          <div className="turno-sesion__opciones" role="group" aria-label="Turno de la sesión">
            {(['morning', 'afternoon'] as const).map((valor) => (
              <button
                key={valor}
                type="button"
                className="turno-sesion__opcion"
                aria-pressed={valor === turno}
                disabled={cambiandoTurno || ctx === null || preferencias === undefined}
                onClick={() => cambiarTurno(valor)}
              >
                {valor === 'morning' ? 'Por la mañana' : 'Por la tarde'}
              </button>
            ))}
          </div>
          {session.trainingTimeOfDay === null && (
            <p className="turno-sesion__pista">
              Es tu horario habitual. Al elegir aquí, esta sesión se queda con el suyo.
            </p>
          )}
          {errorTurno !== null && (
            <p className="campo__error" role="alert">
              {errorTurno}
            </p>
          )}
        </section>
      )}

      {!yaCompletada && (
        <Boton ancho="completo" onClick={empezar}>
          {checkin ? 'Empezar entrenamiento' : 'Continuar al check-in'}
        </Boton>
      )}

      {yaCompletada && (
        <Tarjeta
          titulo="Sesión ya registrada"
          descripcion="El historial de una sesión terminada no cambia aunque después se edite la plantilla."
        />
      )}
    </>
  );
}
